import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { format, startOfDay, addMinutes, isSameDay, isToday } from "date-fns";
import styles from "./Scheduler.module.scss";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { Icon } from "../Icon/icon";
import { Tooltip } from "react-tooltip";
import { AnimatePresence, motion } from "framer-motion";

const TIME_SLOT_INTERVAL = 30;
const START_HOUR = 0;
const END_HOUR = 24;
const PIXELS_PER_MINUTE = 4;

const timelineWidth = (END_HOUR - START_HOUR) * 60 * PIXELS_PER_MINUTE;

interface Event {
  title: string | ReactNode;
  start?: Date;
  end?: Date;
  groupId: string;
}

interface SchedulerProps {
  viewDate?: Date;
  events?: Event[];
  noEventsText?: string;
  groups?: {
    id: string;
    title: string;
    content?: ReactNode;
  }[];
  isLoading?: boolean;
  subscribeToTimeLabel?: string;
  onEventClick?: (event: Event) => void;
}

export function Scheduler({
  viewDate = new Date(),
  events = [],
  noEventsText = "No events",
  groups = [],
  isLoading,
  subscribeToTimeLabel = "Follow current time",
  onEventClick,
}: SchedulerProps) {
  const now = new Date();
  const minutesSinceStart =
    now.getHours() * 60 + now.getMinutes() - START_HOUR * 60;
  const nowOffset = minutesSinceStart * PIXELS_PER_MINUTE;
  const isViewingToday = isToday(viewDate);
  const timelineRef = useRef<HTMLDivElement & { snapTimeout?: NodeJS.Timeout }>(
    null,
  );
  const mouseXRef = useRef<number | null>(null);
  const isAutoScrolling = useRef(false);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [currentTimeOffset, setCurrentTimeOffset] = useState(nowOffset);
  const [followCurrentTime, setFollowCurrentTime] = useState(isViewingToday);

  const timeSlots = useMemo<Date[]>(() => {
    const slots: Date[] = [];
    for (
      let minutes = START_HOUR * 60;
      minutes < END_HOUR * 60;
      minutes += TIME_SLOT_INTERVAL
    ) {
      slots.push(addMinutes(startOfDay(viewDate), minutes));
    }
    return slots;
  }, [viewDate]);

  // Control position of user mouse position time indicator
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;

    let animationFrameId: number;

    const handleMouseMove = (event: MouseEvent) => {
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const absoluteX = relativeX + timeline.scrollLeft;

      if (Math.abs((mouseXRef.current ?? 0) - absoluteX) > 2) {
        mouseXRef.current = absoluteX;
        animationFrameId = requestAnimationFrame(() => {
          setMouseX(absoluteX);
        });
      }
    };

    timeline.addEventListener("mousemove", handleMouseMove);

    return () => {
      timeline.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Instantly set initial scroll position
  useLayoutEffect(() => {
    if (!timelineRef.current) return;
    timelineRef.current.scrollLeft = Math.max(
      0,
      currentTimeOffset - 60 * PIXELS_PER_MINUTE,
    );
    setFollowCurrentTime(isViewingToday);
  }, []);

  // When user scrolls manually, unsubscribe scroll position from current time
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline || !isViewingToday) return;
    let ignoreScroll = false;
    // Stay subscribed to current time when tab comes back into focus after being hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ignoreScroll = true;
        setTimeout(() => {
          ignoreScroll = false;
        }, 500);
      }
    };
    const handleScroll = () => {
      if (!isAutoScrolling.current && !ignoreScroll) {
        setFollowCurrentTime(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    timeline.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      timeline.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Subscribe scroll position to current time (only when following)
  useEffect(() => {
    if (!followCurrentTime || !timelineRef.current || !isViewingToday) return;

    const timeline = timelineRef.current;
    isAutoScrolling.current = true;
    timeline.scrollTo({
      left: Math.max(0, currentTimeOffset - 60 * PIXELS_PER_MINUTE),
      behavior: "smooth",
    });

    setTimeout(() => {
      isAutoScrolling.current = false;
    }, 1500);
  }, [followCurrentTime, currentTimeOffset]);

  // Update current time indicator position every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const minutesSinceStart =
        now.getHours() * 60 + now.getMinutes() - START_HOUR * 60;
      setCurrentTimeOffset(minutesSinceStart * PIXELS_PER_MINUTE);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Capture vertical scrolling in timeline and convert into horizontal scrolling
  useEffect(() => {
    const container = timelineRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      // Check if the user is scrolling vertically
      if (e.deltaY === 0) return;
      // Prevent vertical scroll
      e.preventDefault();
      // Scroll horizontally instead
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", onWheel);
    };
  }, []);

  // Make event content "sticky" so it's more visible in the timeline
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;

    const updatePositions = () => {
      const timeline = timelineRef.current;
      if (!timeline) return;

      const timelineRect = timeline.getBoundingClientRect();
      const eventEls = timeline.querySelectorAll(`.${styles.event}`);

      eventEls.forEach((el) => {
        const content = el.querySelector<HTMLElement>(
          `.${styles.eventContent}`,
        );
        if (!content) return;

        const eventRect = el.getBoundingClientRect();

        // Skip shifting if content is already overflowing its own container
        if (content.offsetWidth >= eventRect.width) {
          content.style.transform = `translateX(0px)`;
          return;
        }

        const distanceFromContainerLeft = timelineRect.left - eventRect.left;

        // If the event is fully visible (or its left edge is still within the container), don't move
        if (distanceFromContainerLeft <= 0) {
          content.style.transform = `translateX(0px)`;
          return;
        }

        // Otherwise, clamp so the content doesn’t exceed the event’s width
        const maxShift = eventRect.width - content.offsetWidth - 6; // 6px for padding
        const shift = Math.min(distanceFromContainerLeft, maxShift);

        content.style.transform = `translateX(${shift}px)`;
      });
    };

    updatePositions();
    timeline.addEventListener("scroll", updatePositions);
    return () => {
      timeline.removeEventListener("scroll", updatePositions);
    };
  }, [viewDate]);

  return (
    <div
      className={`${styles.scheduleContainer} absoluteMaxWidth`}
      onMouseLeave={() => setMouseX(null)}
    >
      <div className={styles.groupsColumn}>
        {isLoading || groups.length <= 0 ? (
          <div
            className={styles.groupContainer}
            style={{ padding: "var(--wlcm-spacing-md) var(--wlcm-spacing-xl)" }}
          ></div>
        ) : (
          groups.map((group) => (
            <div key={group.id} className={styles.groupContainer}>
              {group.content ? group.content : <p>{group.title}</p>}
            </div>
          ))
        )}
        <div style={{ height: 40 }}></div>
      </div>
      <div ref={timelineRef} className={styles.timelineColumn}>
        {isViewingToday && (
          <div
            className={styles.currentTimeIndicator}
            style={{ left: `${currentTimeOffset}px` }}
          />
        )}
        {mouseX !== null && (
          <div
            className={styles.userMouseTimeIndicator}
            style={{ left: `${mouseX}px` }}
          />
        )}
        <div
          className={styles.timeline}
          style={{ width: `${timelineWidth}px` }}
        >
          {timeSlots.map((slot, index) => (
            <div
              key={index}
              className={styles.timeSlot}
              style={{
                width: `calc(${TIME_SLOT_INTERVAL} * ${PIXELS_PER_MINUTE}px)`,
              }}
            >
              {format(slot, "HH:mm")}
            </div>
          ))}
        </div>
        <div className={styles.events} style={{ width: `${timelineWidth}px` }}>
          {isLoading ? (
            <div className={styles.eventsRow}>
              <div className={styles.fixedCenter}>
                <LoadingSpinner size={40} />
              </div>
            </div>
          ) : events.length <= 0 ? (
            <div className={styles.eventsRow}>
              <p className={styles.fixedCenter}>{noEventsText}</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.id} className={styles.eventsRow}>
                {events
                  .filter(
                    (ev) =>
                      !!ev.start &&
                      !!ev.end &&
                      ev.groupId === group.id &&
                      isSameDay(ev.start, viewDate),
                  )
                  .map((ev) => {
                    const startOffset =
                      (ev.start!.getHours() * 60 +
                        ev.start!.getMinutes() -
                        START_HOUR * 60) *
                      PIXELS_PER_MINUTE;

                    const duration =
                      ((ev.end!.getTime() - ev.start!.getTime()) / 60000) *
                      PIXELS_PER_MINUTE;
                    return (
                      <div
                        key={ev.start!.getTime()}
                        className={styles.event}
                        style={{
                          left: `${startOffset}px`,
                          width: `${duration}px`,
                          cursor: onEventClick ? "pointer" : "default",
                        }}
                        onClick={() => onEventClick?.(ev)}
                      >
                        <div className={styles.eventContent}>
                          <p>
                            <span>
                              {format(ev.start!, "HH:mm")} -{" "}
                              {format(ev.end!, "HH:mm")}
                            </span>
                            <br />
                            {ev.title}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ))
          )}
        </div>
        <AnimatePresence initial={false}>
          {!followCurrentTime && isViewingToday ? (
            <motion.button
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className={styles.followCurrTimeBtn}
              onClick={() => {
                isAutoScrolling.current = true;
                setFollowCurrentTime(true);
              }}
            >
              <Icon name="resume" />
              <span className="sr-only">{subscribeToTimeLabel}</span>
            </motion.button>
          ) : (
            <div style={{ height: 40 }}></div>
          )}
          <Tooltip
            style={{
              backgroundColor: "var(--wlcm-color-malling-purple)",
              color: "black",
            }}
            anchorSelect={`.${styles.followCurrTimeBtn}`}
            content={subscribeToTimeLabel}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}
